import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link2, RefreshCw, Users } from "lucide-react";

const STATE_LABELS = {
  earning: "Earning",
  loading: "Loading screen",
  awaiting_move: "Not moved yet",
  afk: "AFK — paused",
  pending: "First poll",
};

const STATE_STYLES = {
  earning: "border-primary/50 bg-primary/10 text-primary",
  loading: "border-amber-500/50 bg-amber-500/10 text-amber-400",
  awaiting_move: "border-amber-500/50 bg-amber-500/10 text-amber-400",
  afk: "border-red-500/50 bg-red-500/10 text-red-400",
  pending: "border-border text-muted-foreground",
};

export const OnlinePlayers = () => {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const [claimDiscord, setClaimDiscord] = useState("");
  const [claimTarget, setClaimTarget] = useState("");
  const [claims, setClaims] = useState([]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/fini/online"),
        api.get("/admin/players"),
        api.get("/admin/identity-claims"),
      ]);
      setRows(a.data.players);
      setAccounts(b.data);
      setClaims(c.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const link = async (row) => {
    const userId = picks[row.license];
    if (!userId) return toast.error("Pick a portal account first");
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/players/${userId}/link-online`, {
        license_id: row.license,
        discord: row.discord || "",
        fivem: row.fivem || "",
        steam: row.steam || "",
        name: row.name || "",
      });
      toast.success(`${row.name} linked to ${data.username} — points start next poll`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createClaim = async () => {
    const row = rows.find((r) => r.license === claimTarget);
    if (!claimDiscord.trim() || !row) return toast.error("Enter a Discord ID and pick the in-game player");
    try {
      const { data } = await api.post("/admin/identity-claims", {
        portal_discord_id: claimDiscord.trim(),
        license_id: row.license,
        fivem: row.fivem || "",
        steam: row.steam || "",
        game_discord_id: row.discord || "",
        name: row.name || "",
      });
      toast.success(
        data.applied_to
          ? `Applied immediately to ${data.applied_to}`
          : `Saved — ${row.name} attaches automatically when that Discord signs in`
      );
      setClaimDiscord("");
      setClaimTarget("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeClaim = async (id) => {
    await api.delete(`/admin/identity-claims/${id}`);
    toast.success("Pre-authorisation removed");
    load();
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(needle) ||
        (r.license || "").includes(needle) ||
        (r.linked_to || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const linkedCount = rows.filter((r) => r.linked_to).length;

  return (
    <div className="mt-4 border border-border bg-card p-8" data-testid="online-players-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Who's online right now</h3>
          <Badge className="rounded-none uppercase" variant="secondary" data-testid="online-count-badge">
            {linkedCount}/{rows.length} linked
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          <Input
            data-testid="online-search"
            placeholder="Search name or license"
            className="w-60 rounded-none bg-secondary"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="outline" className="rounded-none" onClick={load} disabled={busy} data-testid="refresh-online-btn">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
        A player's portal Discord is not always the Discord running on their gaming PC. When someone
        isn't matching automatically, pick their portal account here and link it — every identifier we
        can see gets attached, and they start earning on the next poll.
      </p>

      <div className="mt-6 border border-primary/30 bg-primary/5 p-6" data-testid="claim-box">
        <h4 className="text-xs uppercase tracking-[0.2em] text-primary">
          Pre-authorise a Discord login
        </h4>
        <p className="mt-2 text-sm text-muted-foreground">
          For players whose portal Discord differs from the Discord on their gaming PC. Paste the Discord
          ID they sign in with, pick who they are in-game, and the identity attaches itself the moment
          they log in — even if the account doesn't exist yet.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Input
            data-testid="claim-discord-input"
            placeholder="Portal Discord ID e.g. 1464130103846899876"
            className="w-80 rounded-none bg-secondary mono"
            value={claimDiscord}
            onChange={(e) => setClaimDiscord(e.target.value)}
          />
          <select
            data-testid="claim-target-select"
            className="min-w-64 rounded-none border border-input bg-secondary px-3 py-2 text-sm"
            value={claimTarget}
            onChange={(e) => setClaimTarget(e.target.value)}
          >
            <option value="">Pick the in-game player…</option>
            {rows.map((r) => (
              <option key={r.license} value={r.license}>
                {r.name} {r.linked_to ? `(already ${r.linked_to})` : ""}
              </option>
            ))}
          </select>
          <Button className="rounded-none" onClick={createClaim} data-testid="claim-save-btn">
            Pre-authorise
          </Button>
        </div>
        {claims.length > 0 && (
          <div className="mt-5 space-y-2 text-sm" data-testid="claims-list">
            {claims.map((c) => (
              <div
                key={c.portal_discord_id}
                className="flex items-center justify-between border-b border-border pb-2"
                data-testid={`claim-row-${c.portal_discord_id}`}
              >
                <span className="mono text-xs">
                  {c.portal_discord_id} → {c.name || c.license_id?.slice(0, 12)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeClaim(c.portal_discord_id)}
                  data-testid={`claim-delete-${c.portal_discord_id}`}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 border border-border">
        <div className="grid grid-cols-12 gap-3 border-b border-border bg-secondary/40 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <div className="col-span-3">In-game name</div>
          <div className="col-span-3">Identifiers</div>
          <div className="col-span-6">Portal account</div>
        </div>
        <div className="max-h-[520px] overflow-auto">
          {filtered.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">No online players match.</p>
          )}
          {filtered.map((r) => (
            <div
              key={r.license || r.discord || r.name}
              className="grid grid-cols-12 items-center gap-3 border-b border-border px-4 py-3 text-sm"
              data-testid={`online-row-${r.license}`}
            >
              <div className="col-span-3 truncate font-semibold">
                {r.name || "unknown"}
                {r.state && (
                  <div
                    className={`mt-1 inline-block border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${STATE_STYLES[r.state] || "border-border text-muted-foreground"}`}
                    data-testid={`state-badge-${r.license}`}
                    title={r.idle_seconds != null ? `still for ${Math.floor(r.idle_seconds / 60)}m` : ""}
                  >
                    {STATE_LABELS[r.state] || r.state}
                  </div>
                )}
              </div>
              <div className="col-span-3 mono text-xs leading-relaxed text-muted-foreground">
                {r.discord && <div className="truncate whitespace-nowrap">discord {r.discord}</div>}
                {r.fivem && <div className="truncate whitespace-nowrap">fivem {r.fivem}</div>}
                {r.steam64 && (
                  <div className="truncate whitespace-nowrap" title={`hex ${r.steam}`}>
                    sid {r.steam64}
                  </div>
                )}
                <div className="truncate whitespace-nowrap">lic {r.license?.slice(0, 12)}…</div>
              </div>              <div className="col-span-6 flex items-center gap-3">
                {r.linked_to ? (
                  <Badge className="rounded-none" data-testid={`linked-badge-${r.license}`}>
                    {r.linked_to} · via {r.matched_by}
                  </Badge>
                ) : (
                  <>
                    <select
                      data-testid={`account-select-${r.license}`}
                      className="flex-1 rounded-none border border-input bg-secondary px-3 py-2 text-sm"
                      value={picks[r.license] || ""}
                      onChange={(e) => setPicks({ ...picks, [r.license]: e.target.value })}
                    >
                      <option value="">Not linked — choose account…</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{`${a.username} (${a.email})`}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      className="rounded-none"
                      disabled={busy}
                      onClick={() => link(r)}
                      data-testid={`link-btn-${r.license}`}
                    >
                      <Link2 className="mr-2 h-3.5 w-3.5" /> Link
                    </Button>
                    {r.code && <span className="mono text-xs text-primary">{r.code}</span>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
